import {
  type default as AnalyticsService,
  AnalyticsEvents,
} from 'uplisting-frontend/services/analytics';

type EventProperties = Record<string, unknown>;

interface IFormStepTrackerOptions {
  /** Identifies the flow in Amplitude, e.g. `onboarding_info`. */
  formName: string;
  /** Properties merged into every form-step event, e.g. `{ propertyId }`. */
  props?: EventProperties;
}

/**
 * Tracks the `form_started` / `form_submitted` / `form_abandoned` funnel for a
 * single form.
 *
 * - `markStarted` fires `form_started` once (idempotent) on first interaction.
 * - `markSubmitted` fires `form_submitted` and suppresses abandonment.
 * - `teardown` fires `form_abandoned` only if the form was started but not
 *   submitted, then resets state. Call it from `willDestroy` in components or
 *   `resetController` in controllers (which persist across visits).
 *
 * Every event carries `formName` plus any `props` supplied at construction.
 */
export default class FormStepTracker {
  readonly #analytics: AnalyticsService;
  readonly #formName: string;
  readonly #baseProps: EventProperties;

  #started = false;
  #submitted = false;

  constructor(analytics: AnalyticsService, options: IFormStepTrackerOptions) {
    this.#analytics = analytics;
    this.#formName = options.formName;
    this.#baseProps = options.props ?? {};
  }

  markStarted(extra: EventProperties = {}): void {
    if (this.#started) {
      return;
    }

    this.#started = true;

    this.#track(AnalyticsEvents.formStarted, extra);
  }

  markSubmitted(extra: EventProperties = {}): void {
    this.#submitted = true;

    this.#track(AnalyticsEvents.formSubmitted, extra);
  }

  teardown(extra: EventProperties = {}): void {
    if (this.#started && !this.#submitted) {
      this.#track(AnalyticsEvents.formAbandoned, extra);
    }

    this.reset();
  }

  reset(): void {
    this.#started = false;
    this.#submitted = false;
  }

  #track(event: AnalyticsEvents, extra: EventProperties): void {
    this.#analytics.trackEvent(event, {
      formName: this.#formName,
      ...this.#baseProps,
      ...extra,
    });
  }
}
